/**
 * composeBrandPrompt — the single deterministic seam that turns the Brand
 * Brain into an output-type-routed, hash-stamped prompt fragment.
 *
 * Every downstream agentic surface (content engine, proposal writer, blog
 * drafts, email sequences) calls this ONE function instead of reaching into
 * scattered voice strings. Determinism is the contract: same brain + scope
 * + budget → byte-identical fragment and hash, so a draft's provenance can
 * prove exactly which brand knowledge shaped it (same spirit as
 * content/promptShaper's receipt + agent/provenance's digest).
 *
 * ADAPTER NOTE (reconciling the earlier artifacts): brain/companyBrain.ts
 * remains the per-ACCOUNT fact store — it becomes a thin re-export adapter
 * over this substrate in a later atom, not modified here. docs/brandDoc.ts
 * feeds one-time IMPORT of its render constants into BrandEntry rows only;
 * neither is a runtime source of prompt voice anymore.
 *
 * PURE: no store, no firebase, no crypto import, no clock.
 */

import type { BrandBrain, BrandEntry, BrandEntryKind, BrandScope } from './schema';
import { getActiveByScope } from './store';

// ---------------------------------------------------------------------------
// Deterministic hash — djb2 (xor variant), no crypto import
// ---------------------------------------------------------------------------

/**
 * djb2 string hash (Bernstein, public-domain algorithm), xor variant,
 * rendered as fixed-width hex with a scheme prefix. Deterministic across
 * platforms — pure 32-bit integer math, no Node crypto.
 */
export function djb2Hash(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h) ^ input.charCodeAt(i);
    h >>>= 0; // keep in uint32 space
  }
  return `djb2:${h.toString(16).padStart(8, '0')}`;
}

// ---------------------------------------------------------------------------
// Token estimation
// ---------------------------------------------------------------------------

/** Cheap deterministic token estimate: ceil(chars / 4). */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/** Canonical render order: rules first, then who-we-are, then evidence. */
const KIND_ORDER: Record<BrandEntryKind, number> = {
  directive: 0,
  positioning: 1,
  voice: 2,
  offer: 3,
  lexicon: 4,
  proof: 5,
};

/** Section headers rendered above each kind group. */
const KIND_LABEL: Record<BrandEntryKind, string> = {
  directive: 'Directives (always follow)',
  positioning: 'Positioning',
  voice: 'Voice',
  offer: 'Offers',
  lexicon: 'Lexicon (preferred language)',
  proof: 'Proof points',
};

export interface ComposedBrandPrompt {
  /** The rendered prompt fragment to inject into a generation prompt. */
  fragment: string;
  /** Deterministic content hash of the fragment ('neutral' for fallback). */
  hash: string;
  /** Exactly which entries made it into the fragment, in render order. */
  injected: BrandEntry[];
  /** True when the token budget forced at least one selected entry out. */
  truncated: boolean;
}

/**
 * Neutral fallback for an empty Brand Brain or a caller-level error path.
 * Never throws, always the same value — downstream provenance records
 * `hash: 'neutral'` to say honestly "no brand knowledge shaped this".
 */
export function brandFallbackFragment(): ComposedBrandPrompt {
  return { fragment: '', hash: 'neutral', injected: [], truncated: false };
}

/** Render one entry as a stable single line. */
function renderEntry(e: BrandEntry): string {
  return `- [${e.id}] ${e.title}: ${e.body}`;
}

/**
 * Deterministically compose the brand prompt fragment for one output scope.
 *
 * Selection: active entries routed to `scope` (including 'all'-scoped).
 * Priority: weight desc, id asc (stable) — the greedy budget fill runs in
 * this order so the LOWEST-weight entries are the first to be dropped.
 * Render: grouped by kind in canonical order
 * (directives → positioning → voice → offer → lexicon → proof).
 * Budget: estimateTokens = ceil(chars/4) per rendered entry line; entries
 * that do not fit are skipped (truncated: true) while smaller lower-priority
 * entries may still fill the remaining budget.
 *
 * Same inputs → byte-identical `fragment` and `hash`. Empty selection →
 * {@link brandFallbackFragment} (never throws).
 */
export function composeBrandPrompt(
  brain: BrandBrain,
  scope: BrandScope,
  tokenBudget: number,
): ComposedBrandPrompt {
  const candidates = getActiveByScope(brain, scope); // weight desc, id asc
  if (candidates.length === 0) return brandFallbackFragment();

  // Greedy fill by priority within the token budget.
  const chosen: BrandEntry[] = [];
  let truncated = false;
  let used = 0;
  for (const e of candidates) {
    const cost = estimateTokens(renderEntry(e));
    if (used + cost <= tokenBudget) {
      chosen.push(e);
      used += cost;
    } else {
      truncated = true;
    }
  }
  if (chosen.length === 0) {
    // Budget too small for even one entry — honest neutral, flagged truncated.
    return { ...brandFallbackFragment(), truncated: true };
  }

  // Render grouped by kind in canonical order; within a kind the priority
  // order (weight desc, id asc) is already stable from getActiveByScope.
  const injected = [...chosen].sort((a, b) => {
    const k = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
    if (k !== 0) return k;
    if (b.weight !== a.weight) return b.weight - a.weight;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  const lines: string[] = [`BRAND BRAIN (scope: ${scope})`];
  let currentKind: BrandEntryKind | null = null;
  for (const e of injected) {
    if (e.kind !== currentKind) {
      currentKind = e.kind;
      lines.push('', `${KIND_LABEL[e.kind]}:`);
    }
    lines.push(renderEntry(e));
  }
  const fragment = lines.join('\n');

  return { fragment, hash: djb2Hash(fragment), injected, truncated };
}
